import React, { useContext, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { Formik } from 'formik';
import { Form, Spinner } from 'react-bootstrap';
import Button from 'react-bootstrap/Button';
import { PencilFill, Plus } from 'react-bootstrap-icons';
import * as yup from 'yup';
import ManualStockItemCreationComponent from './ManualStockItemCreationComponent';
import { createStockItem } from '../../clients/product_client';
import {
  checkItemsForUseStatus,
  showToast,
  updateProductStockInProductList,
} from '../../config_and_helpers/helpers';
import { AppContext } from '../../App';
import CustomProductContextHook from '../../pages/Product/CustomProductContextHook';
import NumberInput from '../Generic/NumberInput';
import { parseNumericInput, yupNumericField } from '../../utils/numberInput';
import { bulkStockItemsAffectOpeningHistory } from '../../utils/productOpeningStatistics';
import { parseStoragePlace } from '../../utils/storagePlace';

/**
 * @typedef {import('yup').ObjectSchema} ObjectSchema
 * @typedef {import('yup').StringSchema} StringSchema
 * @typedef {import('yup').DateSchema} DateSchema
 */
const stockItemSchema = yup.object().shape({
  batch: yup.string(),
  expiry: yup.date(),
  openedOn: yup.date(),
});

/**
 * Schema definition for creating stock items manually.
 * @type {import('yup').ObjectSchema}
 */
const groupSchema = yup.object().shape({
  quantity: yupNumericField({
    required: true,
    integerOnly: true,
    min: 1,
    typeError: 'Quantity is required',
  }),
  item: stockItemSchema,
});

const createStockItemsManuallySchema = yup.object().shape({
  groups: yup.array().of(groupSchema).min(1, 'At least one group is required'),
});

/**
 * Represents a modal component for creating stock items manually.
 *
 * @param {Object} ManualStockItemCreationModal - The data required for the modal component.
 * @param {string} ManualStockItemCreationModal.productName - The name of the product.
 * @param {number} ManualStockItemCreationModal.productId - The ID of the product.
 * @param {string} ManualStockItemCreationModal.productSubStock - The sub stock of the product.
 * @param {function} ManualStockItemCreationModal.onSuccessfulSubmit - Callback function when submission is successful.
 * @param {function} [ManualStockItemCreationModal.onOpeningHistoryChange] - Refetch product detail when created items affect opening history.
 *
 * @returns {JSX.Element} The ManualStockItemCreationModal component.
 */
const ManualStockItemCreationModal = ({
  productName,
  productId,
  productSubStock,
  onSuccessfulSubmit,
  updateProductLogs,
  product,
  onOpeningHistoryChange,
  storageNodes = null,
}) => {
  // Use the context hook to fetch the token for communication with the server side
  const { token } = useContext(AppContext);
  // Use the context hook to fetch the array of products and it's setting function
  const { products, setProducts } = CustomProductContextHook();

  // State used to manage the isSubmitting status
  const [isSubmitting, setIsSubmitting] = useState(false);
  // state used to manage the showing of the modal
  const [showModal, setShowModal] = useState(false);
  // State used to manage groups of items to create
  // Each group has: { quantity, itemTemplate }
  const [groups, setGroups] = useState([
    {
      quantity: 1,
      itemTemplate: {
        opened_by_name: '',
        batch: '',
        expiry: '',
        in_use: false,
        opened_on: '',
        item_sub_stock: productSubStock,
        storage_node_id: null,
        slot_index: null,
      },
    },
  ]);

  // Function to add a new group
  const addGroup = () => {
    setGroups([
      ...groups,
      {
        quantity: 1,
        itemTemplate: {
          opened_by_name: '',
          batch: '',
          expiry: '',
          in_use: false,
          opened_on: '',
          item_sub_stock: productSubStock,
          storage_node_id: null,
          slot_index: null,
        },
      },
    ]);
  };

  // Function to remove a group
  const removeGroup = (index) => {
    if (groups.length === 1) {
      return; // Prevent removing the last group
    }
    const newGroups = [...groups];
    newGroups.splice(index, 1);
    setGroups(newGroups);
  };

  // Function to update a group's quantity
  const updateGroupQuantity = (index, quantity) => {
    const newGroups = [...groups];
    newGroups[index].quantity = quantity;
    setGroups(newGroups);
  };

  // Function to update a group's item template
  const updateGroupItemTemplate = (groupIndex, field, value) => {
    const newGroups = [...groups];
    newGroups[groupIndex].itemTemplate[field] = value;
    setGroups(newGroups);
  };

  // Function to show the modal
  const handleShow = () => setShowModal(true);

  // Function to handle the closing of the modal
  const handleClose = () => {
    setShowModal(false);
    setGroups([
      {
        quantity: 1,
        itemTemplate: {
          opened_by_name: '',
          batch: '',
          expiry: '',
          in_use: false,
          opened_on: '',
          item_sub_stock: productSubStock,
          storage_node_id: null,
          slot_index: null,
        },
      },
    ]);
  };

  // Function used to handle the submission of the item data to the server
  const handleSubmit = (formikValues) => {
    const prepared = [];
    for (const group of groups) {
      const template = { ...group.itemTemplate };
      if (storageNodes) {
        const place = parseStoragePlace(
          storageNodes,
          template.storage_node_id,
          template.slot_index
        );
        if (place.error) {
          showToast(place.error, 'warning', 'top-right', 3500);
          return;
        }
        template.storage_node_id = place.storage_node_id;
        template.slot_index = place.slot_index;
      } else {
        delete template.storage_node_id;
        delete template.slot_index;
      }
      prepared.push({ quantity: group.quantity, itemTemplate: template });
    }
    setIsSubmitting(true);

    // Generate array of items from all groups
    const itemsToCreate = [];
    let totalQuantity = 0;

    prepared.forEach((group) => {
      const groupItems = Array.from({ length: group.quantity }, () => ({
        ...group.itemTemplate,
      }));
      itemsToCreate.push(...groupItems);
      totalQuantity += group.quantity;
    });

    // Call the creation axios function
    createStockItem(token, productId, itemsToCreate, true, product).then(response => {
      if (response && response.success) {
        setTimeout(() => {
          // Update the product array to reflect the changes on the client side
          setProducts(
            updateProductStockInProductList({
              products: products, // Array of products to update
              productId: productId, // ID of the specific product being updated
              operation: null, // (Optional) Operation to perform (e.g., "add" or "subtract"), not used here
              manualCreationValue: totalQuantity, // (Optional) Manual value to add to the stock
            })
          );
          if (onSuccessfulSubmit) {
            onSuccessfulSubmit({ data: response.data });
          }
          if (
            onOpeningHistoryChange &&
            bulkStockItemsAffectOpeningHistory(itemsToCreate)
          ) {
            onOpeningHistoryChange();
          }
          response.toast(); // Triggers a success toast message.
          handleClose();
          setIsSubmitting(false); // Resets the submitting state.
        }, 1000);
        // Update product logs accordingly
        checkItemsForUseStatus(response.data, updateProductLogs);
      } else {
        // Displays an error toast if the submission fails.
        showToast(
          'An unexpected error occurred. Please try again in a little while.',
          'error',
          'top-right',
          3000
        );
        setIsSubmitting(false); // Resets the submitting state.
      }
    });
  };

  return (
    <div>
      {/*Button to to activate the modal*/}
      <Button variant={'success'} onClick={handleShow}>
        <PencilFill /> Manual Creation
      </Button>

      <Modal
        show={showModal}
        onHide={handleClose}
        backdrop="static"
        enforceFocus={false}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Manual Stock Items Creation for {productName}
          </Modal.Title>
        </Modal.Header>
        <Formik
          validateOnMount={true}
          validationSchema={createStockItemsManuallySchema}
          onSubmit={handleSubmit}
          initialValues={{
            groups: [
              {
                quantity: 1,
                item: {
                  openedBy: '',
                  batch: '',
                  expiry: '',
                  inUse: false,
                  openedOn: '',
                },
              },
            ],
          }}
        >
          {({
            handleChange,
            handleSubmit: formikHandleSubmit,
            values,
            handleBlur,
            touched,
            errors,
            setFieldTouched,
            setFieldValue,
          }) => {
            // Calculate total items to create
            const totalItems = groups.reduce((sum, group) => sum + group.quantity, 0);

            return (
              <Form noValidate onSubmit={formikHandleSubmit}>
                <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                  {groups.map((group, groupIndex) => (
                    <div
                      key={groupIndex}
                      className="mb-4 p-3 border border-secondary rounded"
                      style={{ backgroundColor: '#f8f9fa' }}
                    >
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5 className="mb-0">
                          Group {groupIndex + 1}
                        </h5>
                        {groups.length > 1 && (
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={(e) => {
                              e.preventDefault();
                              removeGroup(groupIndex);
                            }}
                          >
                            Remove Group
                          </Button>
                        )}
                      </div>

                      {/* Quantity input for this group */}
                      <Form.Group className="mb-3">
                        <Form.Label>
                          <strong>Quantity</strong> (How many items with these details)
                        </Form.Label>
                        <NumberInput
                          name={`groups.${groupIndex}.quantity`}
                          integerOnly
                          value={group.quantity}
                          onChange={e => {
                            const parsed = parseNumericInput(e.target.value, {
                              integerOnly: true,
                            });
                            const value = parsed != null && parsed > 0 ? parsed : 1;
                            updateGroupQuantity(groupIndex, value);
                            handleChange(e);
                            setFieldValue(`groups.${groupIndex}.quantity`, value);
                          }}
                          onBlur={handleBlur}
                          isValid={
                            touched.groups?.[groupIndex]?.quantity &&
                            !errors.groups?.[groupIndex]?.quantity &&
                            group.quantity
                          }
                          isInvalid={
                            touched.groups?.[groupIndex]?.quantity &&
                            !!errors.groups?.[groupIndex]?.quantity
                          }
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.groups?.[groupIndex]?.quantity}
                        </Form.Control.Feedback>
                      </Form.Group>

                      {/* Item details form for this group */}
                      <ManualStockItemCreationComponent
                        stockItemIndex={groupIndex}
                        formik={{
                          handleChange,
                          values: {
                            ...values,
                            items: groups.map((g) => ({
                              openedBy: g.itemTemplate.opened_by_name,
                              batch: g.itemTemplate.batch,
                              expiry: g.itemTemplate.expiry,
                              inUse: g.itemTemplate.in_use,
                              openedOn: g.itemTemplate.opened_on,
                              storageNodeId: g.itemTemplate.storage_node_id,
                              slotIndex: g.itemTemplate.slot_index,
                            })),
                          },
                          handleBlur,
                          touched,
                          errors,
                          setFieldTouched,
                          setFieldValue,
                        }}
                        storageNodes={storageNodes}
                        onItemChange={(index, field, value) => {
                          if (field === 'storage_place') {
                            const newGroups = [...groups];
                            newGroups[groupIndex].itemTemplate.storage_node_id =
                              value.storage_node_id;
                            newGroups[groupIndex].itemTemplate.slot_index =
                              value.slot_index;
                            setGroups(newGroups);
                            setFieldValue(
                              `groups.${groupIndex}.item.storageNodeId`,
                              value.storage_node_id
                            );
                            setFieldValue(
                              `groups.${groupIndex}.item.slotIndex`,
                              value.slot_index
                            );
                            return;
                          }
                          updateGroupItemTemplate(groupIndex, field, value);
                          // Also update formik values for validation
                          const formikFieldMap = {
                            opened_by_name: 'openedBy',
                            batch: 'batch',
                            expiry: 'expiry',
                            in_use: 'inUse',
                            opened_on: 'openedOn',
                            storage_node_id: 'storageNodeId',
                            slot_index: 'slotIndex',
                          };
                          const formikField = formikFieldMap[field] || field;
                          setFieldValue(`groups.${groupIndex}.item.${formikField}`, value);
                        }}
                        onItemDelete={null}
                        showDeleteButton={false}
                      />
                    </div>
                  ))}

                  {/* Add Group button */}
                  <Button
                    variant="outline-success"
                    onClick={(e) => {
                      e.preventDefault();
                      addGroup();
                    }}
                    className="w-100"
                  >
                    <Plus size={20} className="me-2" />
                    Add Another Group
                  </Button>
                </Modal.Body>
                <Modal.Footer>
                  {isSubmitting ? (
                    <Button variant="primary" disabled>
                      <Spinner
                        size="sm"
                        as="span"
                        animation="border"
                        role="status"
                        aria-hidden="true"
                      />
                    </Button>
                  ) : (
                    <Button variant="primary" type="submit">
                      {`Create ${totalItems} Item${totalItems !== 1 ? 's' : ''}`}
                    </Button>
                  )}

                  <Button variant="secondary" onClick={handleClose}>
                    Close
                  </Button>
                </Modal.Footer>
              </Form>
            );
          }}
        </Formik>
      </Modal>
    </div>
  );
};
export default ManualStockItemCreationModal;
